import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { 
  Star,
  TrendingUp,
  DollarSign,
  MapPin,
  Users,
  Building,
  ExternalLink,
  Heart,
  ArrowLeft,
  Download,
  RefreshCw,
  Filter,
  CheckCircle,
  AlertTriangle,
  Info
} from "lucide-react";
import { RoleMatch, CareerMatchReport } from "@/types/careerMatch";

interface CareerMatchResultsProps {
  report: CareerMatchReport;
  onBack: () => void;
  onSaveReport?: (reportName: string) => void;
  onRegenerateWithFilters?: () => void;
}

export function CareerMatchResults({ 
  report, 
  onBack, 
  onSaveReport, 
  onRegenerateWithFilters 
}: CareerMatchResultsProps) {
  const [favorites, setFavorites] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState<RoleMatch | null>(null);
  const [viewMode, setViewMode] = useState<'all' | 'favorites' | 'top'>('all');
  
  const roleMatches = report.role_matches || [];
  const topMatches = roleMatches.slice(0, 5);
  const displayMatches = viewMode === 'all' ? roleMatches : 
                        viewMode === 'favorites' ? roleMatches.filter(r => favorites.includes(r.canonical_title)) :
                        topMatches;

  const toggleFavorite = (title: string) => {
    setFavorites(prev => 
      prev.includes(title) 
        ? prev.filter(t => t !== title)
        : [...prev, title]
    );
  };

  const formatSalary = (salary: any) => {
    if (!salary) return 'Not available';
    return `$${salary.p25?.toLocaleString()} - $${salary.p75?.toLocaleString()}`;
  };

  const getFitScoreColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600 bg-green-100';
    if (score >= 0.6) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'growing': return <TrendingUp className="w-4 h-4 text-green-600" />;
      case 'declining': return <AlertTriangle className="w-4 h-4 text-red-600" />;
      default: return <Info className="w-4 h-4 text-blue-600" />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Assessment
          </Button>
          <div className="text-center">
            <div className="text-sm text-muted-foreground">Career Match Report</div>
            <div className="text-lg font-semibold">{roleMatches.length} Matches Found</div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Button variant="outline" size="sm" onClick={onRegenerateWithFilters}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refine
            </Button>
          </div>
        </div>
      </header>

      {/* Summary Stats */}
      <div className="border-b border-border py-6">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Matches</p>
                    <p className="text-2xl font-bold">{roleMatches.length}</p>
                  </div>
                  <Star className="w-8 h-8 text-primary" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Top Fit Score</p>
                    <p className="text-2xl font-bold">{Math.round((roleMatches[0]?.fit_score || 0) * 100)}%</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-green-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Avg. Salary Range</p>
                    <p className="text-xl font-bold">
                      ${Math.round(roleMatches.reduce((sum, r) => sum + (r.salary?.p50 || 0), 0) / roleMatches.length || 0).toLocaleString()}
                    </p>
                  </div>
                  <DollarSign className="w-8 h-8 text-primary" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Favorites</p>
                    <p className="text-2xl font-bold">{favorites.length}</p>
                  </div>
                  <Heart className="w-8 h-8 text-red-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="border-b border-border">
        <div className="container mx-auto px-4">
          <Tabs value={viewMode} onValueChange={(value: any) => setViewMode(value)} className="w-full">
            <TabsList className="h-12">
              <TabsTrigger value="all">All Matches ({roleMatches.length})</TabsTrigger>
              <TabsTrigger value="top">Top 5 Matches</TabsTrigger>
              <TabsTrigger value="favorites">Favorites ({favorites.length})</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Results Grid */}
      <main className="py-8 px-4">
        <div className="container mx-auto">
          <div className="grid gap-4">
            {displayMatches.map((match, index) => (
              <Card key={match.canonical_title} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-xl font-semibold">{match.canonical_title}</h3>
                            <Badge 
                              className={`${getFitScoreColor(match.fit_score)} text-xs`}
                            >
                              {Math.round(match.fit_score * 100)}% Match
                            </Badge>
                            {match.growth_delta > 0 && (
                              <Badge variant="outline" className="text-green-600">
                                +{Math.round(match.growth_delta * 100)}% Growth Potential
                              </Badge>
                            )}
                          </div>
                          
                          {match.alias_titles && match.alias_titles.length > 0 && (
                            <p className="text-sm text-muted-foreground mb-3">
                              Also known as: {match.alias_titles.join(', ')}
                            </p>
                          )}
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                            <div className="flex items-center gap-2">
                              <DollarSign className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm">{formatSalary(match.salary)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Building className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm">{match.market?.openings_estimate || 0} openings</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm">{match.competition?.avg_applicants || 0} avg applicants</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {getTrendIcon(match.hiring_activity?.trend || 'stable')}
                              <span className="text-sm capitalize">{match.hiring_activity?.trend || 'stable'}</span>
                            </div>
                          </div>
                          
                          {match.reasons && match.reasons.length > 0 && (
                            <div className="mb-3">
                              <p className="text-sm font-medium text-green-700 mb-1">Why it's a good fit:</p>
                              <ul className="text-sm text-muted-foreground">
                                {match.reasons.slice(0, 2).map((reason, i) => (
                                  <li key={i} className="flex items-center gap-2">
                                    <CheckCircle className="w-3 h-3 text-green-600" />
                                    {reason}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2 ml-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleFavorite(match.canonical_title)}
                      >
                        <Heart 
                          className={`w-4 h-4 ${
                            favorites.includes(match.canonical_title) 
                              ? 'fill-red-500 text-red-500' 
                              : 'text-muted-foreground'
                          }`} 
                        />
                      </Button>
                      
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm">
                            View Details
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>{match.canonical_title}</DialogTitle>
                            <DialogDescription>
                              Detailed analysis and market insights
                            </DialogDescription>
                          </DialogHeader>
                          
                          <div className="space-y-6">
                            {/* Fit Score Breakdown */}
                            <div>
                              <h4 className="font-semibold mb-3">Fit Score: {Math.round(match.fit_score * 100)}%</h4>
                              <Progress value={match.fit_score * 100} className="mb-2" />
                              {match.growth_delta > 0 && (
                                <p className="text-sm text-muted-foreground">
                                  Growth potential: +{Math.round(match.growth_delta * 100)}% with skill development
                                </p>
                              )}
                            </div>
                            
                            {/* Strengths and Gaps */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {match.strengths && match.strengths.length > 0 && (
                                <div>
                                  <h4 className="font-semibold text-green-700 mb-2">Your Strengths</h4>
                                  <ul className="space-y-1">
                                    {match.strengths.map((strength, i) => (
                                      <li key={i} className="flex items-start gap-2 text-sm">
                                        <CheckCircle className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                                        {strength}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              
                              {match.gaps && match.gaps.length > 0 && (
                                <div>
                                  <h4 className="font-semibold text-orange-700 mb-2">Skills to Develop</h4>
                                  <ul className="space-y-1">
                                    {match.gaps.map((gap, i) => (
                                      <li key={i} className="flex items-start gap-2 text-sm">
                                        <AlertTriangle className="w-3 h-3 text-orange-600 mt-0.5 flex-shrink-0" />
                                        {gap}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                            
                            {/* Market Data */}
                            <div>
                              <h4 className="font-semibold mb-3">Market Intelligence</h4>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <Card>
                                  <CardContent className="p-4">
                                    <div className="text-center">
                                      <DollarSign className="w-6 h-6 text-primary mx-auto mb-2" />
                                      <p className="text-sm text-muted-foreground">Salary Range</p>
                                      <p className="font-semibold">{formatSalary(match.salary)}</p>
                                      {match.salary?.p50 && (
                                        <p className="text-xs text-muted-foreground">
                                          Median: ${match.salary.p50.toLocaleString()}
                                        </p>
                                      )}
                                    </div>
                                  </CardContent>
                                </Card>
                                
                                <Card>
                                  <CardContent className="p-4">
                                    <div className="text-center">
                                      <Building className="w-6 h-6 text-primary mx-auto mb-2" />
                                      <p className="text-sm text-muted-foreground">Job Openings</p>
                                      <p className="font-semibold">{match.market?.openings_estimate || 0}</p>
                                      <p className="text-xs text-muted-foreground">
                                        Avg posting age: {match.market?.median_posting_age_days || 0} days
                                      </p>
                                    </div>
                                  </CardContent>
                                </Card>
                                
                                <Card>
                                  <CardContent className="p-4">
                                    <div className="text-center">
                                      <Users className="w-6 h-6 text-primary mx-auto mb-2" />
                                      <p className="text-sm text-muted-foreground">Competition</p>
                                      <p className="font-semibold">{match.competition?.avg_applicants || 0} applicants</p>
                                      <p className="text-xs text-muted-foreground">
                                        ~{match.competition?.est_qualified || 0} qualified
                                      </p>
                                    </div>
                                  </CardContent>
                                </Card>
                              </div>
                            </div>
                            
                            {/* Job Examples */}
                            {match.examples && match.examples.length > 0 && (
                              <div>
                                <h4 className="font-semibold mb-3">Recent Job Postings</h4>
                                <div className="space-y-2">
                                  {match.examples.slice(0, 3).map((example, i) => (
                                    <div key={i} className="border rounded-lg p-3">
                                      <div className="flex justify-between items-start">
                                        <div>
                                          <p className="font-medium">{example.title}</p>
                                          <p className="text-sm text-muted-foreground">{example.company}</p>
                                          <div className="flex items-center gap-4 mt-1">
                                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                                              <MapPin className="w-3 h-3" />
                                              {example.location}
                                            </span>
                                            <span className="text-xs text-muted-foreground">
                                              {example.posted_days_ago} days ago
                                            </span>
                                          </div>
                                        </div>
                                        {example.apply_url && example.apply_url !== '#' && (
                                          <Button variant="outline" size="sm" asChild>
                                            <a href={example.apply_url} target="_blank" rel="noopener noreferrer">
                                              <ExternalLink className="w-3 h-3 mr-1" />
                                              Apply
                                            </a>
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {displayMatches.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No matches found for the selected filter.</p>
              <Button variant="outline" className="mt-4" onClick={() => setViewMode('all')}>
                View All Matches
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}